import * as React from 'react';
import { cn } from '@utils/cn.ts';
import { IconEl } from '@utils/types.ts';

type ButtonProps = {
  size: 'sm' | 'md' | 'lg';
  label: string;
  type: 'primary' | 'secondary' | 'error';
  variant: 'fill' | 'outline';
  leftIcon?: IconEl;
  rightIcon?: IconEl;

  moveLeftIcon?: boolean;
  disabled?: boolean;
  onClick?: (e: any) => void;

  buttonType?: 'button' | 'submit' | 'reset';
  className?: string;
};

export const Button: React.FC<ButtonProps> = ({
  label,
  variant,
  size,
  type,
  moveLeftIcon = false,
  onClick,
  leftIcon,
  rightIcon,
  disabled = false,
  buttonType = 'button',
  className,
}) => {
  const commonDef = `rounded-full inline-flex items-center justify-self-start justify-center ${className} text-nowrap ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`;
  const sizeDef = { 'h-[32px] px-2 utility-body-s-bold': size === 'sm', 'h-[40px] p-4 text-body-m-bold': size === 'md', 'h-[54px] p-6 ': size === 'lg' };
  const colorsDef = {
    'bg-surface-dark-primary text-inverted hover:bg-dark-hover-primary': type === 'primary' && variant === 'fill' && !disabled,
    'bg-surface-dark-secondary text-white hover:bg-surface-darker-secondary': type === 'secondary' && variant === 'fill' && !disabled,
    'bg-surface-dark-error text-white hover:bg-surface-darker-error': type === 'error' && variant === 'fill' && !disabled,

    // Outline styles
    'bg-white outline outline-2 outline-offset-[-1px] outline-none outline-surface-dark-primary text-surface-dark-primary hover:outline-dark-hover-primary hover:text-dark-hover-primary':
      type === 'primary' && variant === 'outline' && !disabled,
    'outline outline-2 outline-offset-[-1px] outline-none outline-surface-dark-secondary text-surface-dark-secondary hover:outline-surface-darker-secondary hover:text-surface-darker-secondary':
      type === 'secondary' && variant === 'outline' && !disabled,
    'outline outline-2 outline-offset-[-1px] outline-none  outline-surface-dark-error text-surface-dark-error hover:outline-surface-darker-error hover:text-surface-darker-error':
      type === 'error' && variant === 'outline' && !disabled,

    'bg-controls-surface-light-disabled text-controls-text-light-disabled': variant === 'fill' && disabled,
    'bg-controls-surface-light-disabled text-controls-text-light-disabled outline-controls-text-light-disabled outline-2 outline-offset-[-1px] outline-none':
      variant === 'outline' && disabled,
  };

  return (
    <button type={buttonType} onClick={e => onClick && !disabled && onClick(e)} className={cn(commonDef, sizeDef, colorsDef)}>
      {leftIcon && (
        <span
          className={[
            size === 'sm' ? 'w-[18px] h-[18px]' : '',
            size === 'md' ? 'md:w-6 md:h-6' : '',
            size === 'lg' ? 'md:w-8 md:h-6=8' : '',
            moveLeftIcon === true ? 'ml-[-10px]' : '',
            'inline-flex items-center justify-center shrink-0',
          ].join(' ')}
        >
          {leftIcon}
        </span>
      )}

      <span className="">{label}</span>

      {rightIcon && (
        <span
          className={[
            size === 'sm' ? 'w-[18px] h-[18px]' : '',
            size === 'md' ? 'md:w-6 md:h-6' : '',
            size === 'lg' ? 'md:w-8 md:h-6=8' : '',
            'inline-flex items-center justify-center shrink-0',
          ].join(' ')}
        >
          {rightIcon}
        </span>
      )}
    </button>
  );
};
